import { api } from "@/lib/api";

// Client-side guardrails (parity requirement): images + common docs, hard-capped at 25MB.
export const MAX_FILE_BYTES = 25 * 1024 * 1024;
export const ALLOWED_FILE_MIME = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "application/pdf",
  "text/plain",
  "text/csv",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];
export const FILE_ACCEPT = ALLOWED_FILE_MIME.join(",");

// Media (v1.2.2 parity): audio (Voice Memos, songs) + video ride the same chunked pipeline.
// Audio uploads as kind "file" with an audio/* mime; video as kind "video". 20MB media cap.
export const MAX_MEDIA_BYTES = 20 * 1024 * 1024;
export const AUDIO_ACCEPT = "audio/*,.m4a,.mp3,.wav,.aac,.ogg,.flac";
export const VIDEO_ACCEPT = "video/*,.mp4,.mov,.webm,.m4v";

// base64 characters per chunk (~384KB raw) — small enough to slide under proxy body limits.
const CHUNK = 512 * 1024;

export function validateFile(file) {
  if (!file) return "No file selected.";
  if (file.size > MAX_FILE_BYTES) return "File is too large (max 25MB).";
  if (!ALLOWED_FILE_MIME.includes(file.type)) return "Unsupported file type — images and documents only.";
  return null;
}

// Audio/video share a 20MB cap and are matched by mime prefix (browsers report audio/*, video/*).
export function validateMedia(file, kind /* "audio" | "video" */) {
  if (!file) return "No file selected.";
  if (file.size > MAX_MEDIA_BYTES) return `That ${kind} is too large (max 20MB).`;
  const prefix = kind === "audio" ? "audio/" : "video/";
  if (!(file.type || "").startsWith(prefix)) return `Please choose ${kind === "audio" ? "an audio" : "a video"} file.`;
  return null;
}

export function humanSize(bytes = 0) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function readAsBase64(file) {
  return new Promise((res, rej) => {
    const fr = new FileReader();
    fr.onload = () => res(String(fr.result).split(",")[1] || "");
    fr.onerror = () => rej(new Error("Couldn't read that file."));
    fr.readAsDataURL(file);
  });
}

// Chunked base64 upload: init → chunk* → complete. Returns the completed-file response.
export async function uploadFileInChunks(conversationId, file, onProgress) {
  // Backend /files/init constrains kind to ^(file|video)$ — images upload as "file"
  // and are rendered inline from their image/* mime type.
  const kind = file.type.startsWith("video/") ? "video" : "file";
  const b64 = await readAsBase64(file);
  const init = await api.filesInit({
    conversation_id: conversationId,
    name: file.name,
    mime: file.type || "application/octet-stream",
    size: file.size,
    kind,
  });
  const uploadId = init?.upload_id || init?.id || init?.uploadId;
  if (!uploadId) throw new Error("Upload could not be started.");
  const total = Math.max(1, Math.ceil(b64.length / CHUNK));
  for (let i = 0; i < total; i++) {
    await api.filesChunk({ upload_id: uploadId, index: i, data_b64: b64.slice(i * CHUNK, (i + 1) * CHUNK) });
    onProgress?.(Math.round(((i + 1) / total) * 100));
  }
  return api.filesComplete(uploadId);
}
